import React, { useState } from 'react';
import { FaDatabase, FaServer, FaEnvelope, FaCode, FaExclamationTriangle, FaLock, FaInfoCircle } from 'react-icons/fa';
import './AWSMetrics.css';

// Include formatBytes directly in the component
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Format date helper
const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
};

const AWSMetrics = ({ data }) => {
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  if (!data) {
    return <div className="metrics-loading">Loading AWS metrics...</div>;
  }

  // Check for permission errors specifically
  const hasPermissionError = data.error && data.error.includes('AccessDenied');
  
  // Check which services are available
  const services = data.services || {};
  
  return (
    <div className="aws-metrics-container">
      {data.error && (
        <div className="section-error">
          <p>{data.error}</p>
          <div className="setup-instructions">
            <h4>AWS Setup Instructions</h4>
            {hasPermissionError ? (
              <>
                <p>Your AWS credentials are valid, but the IAM user lacks required permissions.</p>
                <p>Please add the following IAM policy to your user:</p>
              </>
            ) : (
              <p>To connect AWS, add these environment variables:</p>
            )}
            
            {!hasPermissionError && (
              <pre>
                {`REACT_APP_AWS_ACCESS_KEY_ID=your_access_key_id
REACT_APP_AWS_SECRET_ACCESS_KEY=your_secret_access_key
REACT_APP_AWS_REGION=your_aws_region`}
              </pre>
            )}
            
            <pre>
              {`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "s3:ListAllMyBuckets",
        "s3:GetBucketWebsite",
        "s3:GetBucketLocation",
        "s3:GetBucketAcl",
        "s3:GetBucketPolicy",
        "s3:GetBucketTagging",
        "s3:GetBucketVersioning",
        "s3:GetBucketNotification",
        "cloudwatch:GetMetricData",
        "cloudwatch:ListMetrics",
        "cloudwatch:DescribeAlarms",
        "lambda:ListFunctions",
        "lambda:GetFunction",
        "lambda:GetFunctionConfiguration",
        "rds:DescribeDBInstances",
        "rds:DescribeDBClusters",
        "ec2:DescribeInstances",
        "ec2:DescribeInstanceStatus",
        "ses:GetSendStatistics",
        "ses:ListIdentities"
      ],
      "Resource": "*"
    }
  ]
}`}
            </pre>
          </div>
        </div>
      )}

      {/* Missing Permissions Notice */}
      {!data.error && (
        <div className="permissions-notice">
          <h4><FaInfoCircle /> AWS Services Access</h4>
          <div className="services-grid">
            <div className={`service-item ${services.s3?.available ? 'available' : 'unavailable'}`}>
              <div className="service-icon">S3</div>
              <div className="service-status">
                {services.s3?.available ? 'Available' : 'No Access'}
              </div>
            </div>
            <div className={`service-item ${services.cloudwatch?.available ? 'available' : 'unavailable'}`}>
              <div className="service-icon">CloudWatch</div>
              <div className="service-status">
                {services.cloudwatch?.available ? 'Available' : 'No Access'}
              </div>
            </div>
            <div className={`service-item ${services.rds?.available ? 'available' : 'unavailable'}`}>
              <div className="service-icon">RDS</div>
              <div className="service-status">
                {services.rds?.available ? 'Available' : 'No Access'}
              </div>
            </div>
            <div className={`service-item ${services.ec2?.available ? 'available' : 'unavailable'}`}>
              <div className="service-icon">EC2</div>
              <div className="service-status">
                {services.ec2?.available ? 'Available' : 'No Access'}
              </div>
            </div>
            <div className={`service-item ${services.lambda?.available ? 'available' : 'unavailable'}`}>
              <div className="service-icon">Lambda</div>
              <div className="service-status">
                {services.lambda?.available ? 'Available' : 'No Access'}
              </div>
            </div>
            <div className={`service-item ${services.ses?.available ? 'available' : 'unavailable'}`}>
              <div className="service-icon">SES</div>
              <div className="service-status">
                {services.ses?.available ? 'Available' : 'No Access'}
              </div>
            </div>
          </div>
          {Object.values(services).some(service => !service.available) && (
            <div className="missing-permissions">
              <p><FaLock /> Some AWS services are not accessible. To view all metrics, add these permissions to your IAM user:</p>
              <pre>
                {`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [${!services.s3?.available ? '\n        "s3:ListAllMyBuckets",' : ''}${!services.cloudwatch?.available ? '\n        "cloudwatch:GetMetricData",\n        "cloudwatch:DescribeAlarms",' : ''}${!services.lambda?.available ? '\n        "lambda:ListFunctions",' : ''}${!services.rds?.available ? '\n        "rds:DescribeDBInstances",' : ''}${!services.ec2?.available ? '\n        "ec2:DescribeInstances",' : ''}${!services.ses?.available ? '\n        "ses:GetSendStatistics"' : ''}
      ],
      "Resource": "*"
    }
  ]
}`}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* S3 Buckets Section */}
      {data.buckets && data.buckets.length > 0 && (
        <div className="buckets-section">
          <h4>S3 Buckets</h4>
          <div className="buckets-list">
            {data.buckets.map((bucket, index) => {
              // Find matching metric for this bucket if available
              const metric = data.s3Metrics?.find(m => m.Label.includes(bucket.Name));
              const sizeValue = metric?.Values?.[0];
              
              return (
                <div key={index} className="bucket-item">
                  <div className="bucket-name">{bucket.Name}</div>
                  <div className="bucket-created">{formatDate(bucket.CreationDate)}</div>
                  {sizeValue && (
                    <div className="bucket-size">{formatBytes(sizeValue)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* RDS Instances Section */}
      {data.rdsInstances && data.rdsInstances.length > 0 && (
        <div className="rds-section">
          <h4><FaDatabase /> RDS Databases</h4>
          <div className="rds-list">
            {data.rdsInstances.map((instance, index) => (
              <div key={index} className="rds-item">
                <div className="rds-name">{instance.DBInstanceIdentifier}</div>
                <div className="rds-engine">{instance.Engine} {instance.EngineVersion}</div>
                <div className="rds-status">Status: <span className={`status-${instance.DBInstanceStatus.toLowerCase()}`}>{instance.DBInstanceStatus}</span></div>
                <div className="rds-size">Size: {instance.DBInstanceClass}</div>
                <div className="rds-storage">Storage: {instance.AllocatedStorage} GB</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* EC2 Instances Section */}
      {data.ec2Instances && data.ec2Instances.length > 0 && (
        <div className="ec2-section">
          <h4><FaServer /> EC2 Instances</h4>
          <div className="ec2-list">
            {data.ec2Instances.map((instance, index) => (
              <div key={index} className="ec2-item">
                <div className="ec2-name">{instance.InstanceId}</div>
                <div className="ec2-type">{instance.InstanceType}</div>
                <div className="ec2-status">Status: <span className={`status-${instance.State.Name.toLowerCase()}`}>{instance.State.Name}</span></div>
                <div className="ec2-az">Zone: {instance.Placement.AvailabilityZone}</div>
                <div className="ec2-ip">IP: {instance.PublicIpAddress || 'Private'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Lambda Functions Section */}
      {data.lambdaFunctions && data.lambdaFunctions.length > 0 && (
        <div className="lambda-section">
          <h4><FaCode /> Lambda Functions</h4>
          <div className="lambda-list">
            {data.lambdaFunctions.map((func, index) => (
              <div key={index} className="lambda-item">
                <div className="lambda-name">{func.FunctionName}</div>
                <div className="lambda-runtime">{func.Runtime}</div>
                <div className="lambda-memory">Memory: {func.MemorySize} MB</div>
                <div className="lambda-timeout">Timeout: {func.Timeout}s</div>
                <div className="lambda-modified">Last Modified: {formatDate(func.LastModified)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* SES Statistics Section */}
      {data.sesStats && (
        <div className="ses-section">
          <h4><FaEnvelope /> Email Service (SES)</h4>
          <div className="ses-stats">
            <div className="ses-stat-item">
              <div className="ses-stat-label">Sent (24h)</div>
              <div className="ses-stat-value">{data.sesStats.SendDataPoints?.reduce((sum, point) => sum + point.DeliveryAttempts, 0) || 0}</div>
            </div>
            <div className="ses-stat-item">
              <div className="ses-stat-label">Delivery Rate</div>
              <div className="ses-stat-value">
                {data.sesStats.SendDataPoints?.length > 0 ? 
                  `${((1 - data.sesStats.SendDataPoints.reduce((sum, point) => sum + point.Bounces, 0) / 
                  data.sesStats.SendDataPoints.reduce((sum, point) => sum + point.DeliveryAttempts, 1)) * 100).toFixed(2)}%` : 
                  'N/A'}
              </div>
            </div>
            <div className="ses-stat-item">
              <div className="ses-stat-label">Complaint Rate</div>
              <div className="ses-stat-value">
                {data.sesStats.SendDataPoints?.length > 0 ? 
                  `${((data.sesStats.SendDataPoints.reduce((sum, point) => sum + point.Complaints, 0) / 
                  data.sesStats.SendDataPoints.reduce((sum, point) => sum + point.DeliveryAttempts, 1)) * 100).toFixed(2)}%` : 
                  'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Alerts section */}
      {data.alerts && data.alerts.length > 0 && (
        <div className="alerts-section">
          <h4><FaExclamationTriangle /> Alerts</h4>
          <div className="alerts-list">
            {data.alerts.map((alert, index) => (
              <div key={index} className={`alert-item alert-${alert.severity}`}>
                <div className="alert-service">{alert.service}</div>
                <div className="alert-message">{alert.message}</div>
                <div className="alert-time">{new Date(alert.timestamp).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AWSMetrics; 